import React, { useState, useEffect, useRef, useCallback } from "react";
import "./FeedbackPage.css";

// ── Confetti engine ────────────────────────────────────────────────────────────
const CONFETTI_COLORS = [
  "#4a90e8", "#6366f1", "#f472b6", "#fb923c",
  "#34d399", "#fbbf24", "#a78bfa", "#38bdf8",
];

function spawnParticles(canvasW, canvasH, count = 150) {
  const particles = [];
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 4 + Math.random() * 8;
    particles.push({
      x: canvasW / 2 + (Math.random() - 0.5) * 200,
      y: canvasH * 0.35,
      vx: Math.cos(angle) * speed * (Math.random() > 0.5 ? 1 : -1),
      vy: -6 - Math.random() * 10,
      gravity: 0.28 + Math.random() * 0.12,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      w: 7 + Math.random() * 8,
      h: 3 + Math.random() * 5,
      rot: Math.random() * Math.PI * 2,
      rotSpeed: (Math.random() - 0.5) * 0.22,
      alpha: 1,
      shape: Math.random() > 0.5 ? "rect" : "circle",
    });
  }
  return particles;
}

function useConfetti() {
  const canvasRef = useRef(null);
  const stateRef = useRef({ particles: [], animId: null });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const fire = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const s = stateRef.current;

    if (s.animId) cancelAnimationFrame(s.animId);
    s.particles = spawnParticles(canvas.width, canvas.height, 160);
    setTimeout(() => {
      s.particles = [
        ...s.particles,
        ...spawnParticles(canvas.width, canvas.height, 80),
      ];
    }, 300);

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      s.particles = s.particles.filter((p) => p.alpha > 0.01);
      for (const p of s.particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.vx *= 0.99;
        p.rot += p.rotSpeed;
        p.alpha -= 0.012;
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.shape === "circle") {
          ctx.beginPath();
          ctx.arc(0, 0, p.w / 2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        }
        ctx.restore();
      }
      if (s.particles.length > 0) {
        s.animId = requestAnimationFrame(loop);
      } else {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        s.animId = null;
      }
    };
    loop();
  }, []);

  return { canvasRef, fire };
}

// ── Data ───────────────────────────────────────────────────────────────────────
const OPTIONS = [
  { id: "amazing", emoji: "🤩", label: "Amazingggg", sub: "We're so glad you loved it! 🚀" },
  { id: "loved",   emoji: "😍", label: "Loved it",   sub: "That made our day! ❤️" },
  { id: "fun",     emoji: "😊", label: "It was fun", sub: "Fun is what we aim for! 😄" },
  { id: "okay",    emoji: "👍", label: "It was okay", sub: "Thanks for being honest. We'll keep improving! 💪" },
];

// ── CheckIcon ─────────────────────────────────────────────────────────────────
function CheckIcon() {
  return (
    <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M2 7l3.5 3.5L12 3.5"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ── FeedbackCard ──────────────────────────────────────────────────────────────
function FeedbackCard({ option, selected, dimmed, onClick, animDelay }) {
  const emojiRef = useRef(null);

  const handleClick = () => {
    // Emoji pop
    if (emojiRef.current) {
      emojiRef.current.classList.remove("emoji--pop");
      void emojiRef.current.offsetWidth; // reflow
      emojiRef.current.classList.add("emoji--pop");
    }
    onClick(option);
  };

  return (
    <div
      className={[
        "fb-card",
        selected ? "fb-card--selected" : "",
        dimmed  ? "fb-card--dimmed"   : "",
      ].join(" ")}
      style={{ animationDelay: `${animDelay}s` }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
    >
      <div className="fb-card__check">
        <CheckIcon />
      </div>
      <span className="fb-card__emoji" ref={emojiRef}>{option.emoji}</span>
      <span className="fb-card__label">{option.label}</span>
    </div>
  );
}

// ── FeedbackPage (main) ───────────────────────────────────────────────────────
/**
 * Props:
 *   onSelect(label: string) — called when user confirms a rating
 */
export default function FeedbackPage({ onSelect }) {
  const [selected, setSelected]     = useState(null);
  const [locked, setLocked]         = useState(false);
  const { canvasRef, fire }         = useConfetti();

  const handleCardClick = useCallback(
    (option) => {
      if (locked) return;
      if (selected?.id === option.id) return;
      setSelected(option);
      setLocked(true);
      fire();
      // Directly call onSelect after confetti without showing modal
      setTimeout(() => {
        if (typeof onSelect === "function") onSelect(option.label);
      }, 1500);
    },
    [selected, fire, locked, onSelect]
  );

  return (
    <div className="fb-page">
      {/* Confetti canvas */}
      <canvas ref={canvasRef} className="fb-confetti-canvas" />

      {/* Background blobs */}
      <div className="fb-blob fb-blob--1" />
      <div className="fb-blob fb-blob--2" />

      {/* Content */}
      <div className="fb-content">
        <h1 className="fb-title">
          How much did you like<br />this experience?
        </h1>

        <div className="fb-grid">
          {OPTIONS.map((opt, i) => (
            <FeedbackCard
              key={opt.id}
              option={opt}
              selected={selected?.id === opt.id}
              dimmed={selected !== null && selected?.id !== opt.id}
              onClick={handleCardClick}
              animDelay={0.1 + i * 0.1}
            />
          ))}
        </div>

        <p className="fb-footer">Your feedback helps us improve!</p>
      </div>
    </div>
  );
}
